/**
 * 確認のページで「公開する」を押した後の見せ方（純関数・import は型だけ = node --test でそのまま動く）。
 * 2026-10-15 console/confirm-publishing。画面は pages/ConfirmAction.tsx の PublishProgress。
 *
 * WHY（founder 2026-10-15・Storm Ship を本番に出したとき）: 「パブリッシュした後に『Not published yet. It will be published in a moment.』が
 *   出てくると不安だね。せめて、公開準備中ですみたいな表示とアニメーションを作るとかがいいよ」。
 *   以前（2026-10-13 console/confirm-wait）は押した後も一番上が黄色の「まだ公開されていません」で、下に緑の「許可しました」が並んでいた
 *   = 押した人には「失敗した？」と読めた。いまは押した後を 3 つの段にする:
 *     working … 見出し「公開の準備中です」+ 動くバー（動きを減らす設定の人は styles.css の全体の決まりで止まった形）+ 何を待っているかの 1 行
 *     done    … 見出し「公開しました」+ URL と「開く」ボタン
 *     stopped … 見出し「公開されませんでした」+ 何が起きたか + 次にすること（今までの confirmAction.status.* の文 / CLI はコマンドをもう一度）
 *   と、待ちの間の 2 つ:
 *     slow    … 1 分たっても終わらない（CLI だけ = パソコン側で止まっているかも。AI の接続は confirmAction.next.chat が同じことを言う）
 *     unknown … 読み直しの上限（POLL_LIMIT_MS）を過ぎた = 読み直しを止め、「読み込み直す」ボタン（ずっと読み続けない）
 *
 * 「公開しました」の根拠: 確認の状態が used（= 送り直しが本体を通った）。公開の確認の送り直しは activate（その版に切り替える）で、
 *   ファイルは確認の前に送り済み（packages/cli/src/commands/deploy.ts finalizeAndActivate の WHY・activate:false で揃えてから確認）。
 *   = used になった時点で本体はもうその版を配っている。新しい API は要らない（今ある GET /v1/developers/pending-actions/:id で分かる）。
 *   束（初めての公開 = 公開を ON + 公開）は確認のページの id が束の最後（activate）= CLI・MCP とも最後の id を見せる
 *   （packages/cli/src/lib/pendingAction.ts confirmBundleViaLink の last・apps/mcp/src/pendingActions.ts の last）。
 *   それでも念のため、束の途中の行が approved のままなら used と言わない（pendingAction.ts liveStatus）。
 *
 * 言葉: 見える文に中の言葉（CLI・版・環境…）を出さない = confirmPlain.ts PLAIN_FORBIDDEN。CLI は「あなたのパソコンの {cliName}」と言う。
 */

import type { PendingStatus } from './pendingAction.ts';

/** 押した後に本体を読み直す上限（ミリ秒）。12 分 = 承認で期限が +10 分に延びる（packages/cli/src/lib/pendingAction.ts の 3.）+ 余裕 2 分。
 *  それまでに本体が used か expired を返すはず = 上限に当たるのは本体に届かない（回線・スリープ）ときだけ */
export const POLL_LIMIT_MS = 12 * 60_000;
/** 「時間がかかっています」を出すまで（ミリ秒）。CLI の送り直しはふつう数秒（2.5 秒おきに見ている）= 1 分は明らかに遅い */
export const SLOW_AFTER_MS = 60_000;

export type PublishPhase = 'working' | 'slow' | 'unknown' | 'done' | 'stopped';

export interface PublishingView {
  phase: PublishPhase;
  /** ページの見出し（AuthShell の title）のキー */
  title: string;
  /** 見出しの下の 1 行（何を待っているか・どこで開けるか・何が起きたか）。{url} は画面がリンクにする */
  line: string;
  /** その下の 1 行（次にすること）。無ければ出さない */
  next?: string;
  /** 「開く」ボタンの名前のキー（done で URL があるときだけ） */
  open?: string;
  /** 読み直しを続けるか（working・slow だけ。done・stopped・unknown は止める） */
  poll: boolean;
}

/**
 * 押した後の見せ方。公開の確認でない（target null）なら null = 今までの画面（StatusMessage）。
 * status = いまの確認の状態（読み直しの結果・liveStatus）・elapsedMs = 押してから（ページを開いてから）の時間・hasUrl = 公開先の URL が読めたか
 */
export function publishingView(input: { status: PendingStatus; target: 'live' | 'test' | null; cli: boolean; elapsedMs: number; hasUrl: boolean; cliGone?: boolean }): PublishingView | null {
  const { status, target, cli, elapsedMs, hasUrl } = input;
  // pending は押す前 = この見せ方の外（ConfirmForm が描く）
  if (!target || status === 'pending') return null;
  const test = target === 'test' ? 'Test' : '';
  // 押した後に CLI が送り直す前に止まった（2026-10-15 core/confirm-done・pendingAction.ts cliStoppedWaiting）: 本体の「60 秒見に来ていない」で
  // 「準備中」を続けずに「公開されませんでした・コマンドをもう一度」。押す前に止まっていた場合は本体が承認を断る = ここには来ない（ConfirmAction.tsx NotWaiting）
  if (status === 'approved' && cli && input.cliGone) return { phase: 'stopped', title: 'confirmPlain.stopped.title', line: 'confirmPlain.stopped.notWaitingCli', poll: false };
  if (status === 'approved') {
    if (elapsedMs >= POLL_LIMIT_MS) return { phase: 'unknown', title: 'confirmPlain.publishing.unknownTitle', line: 'confirmPlain.publishing.unknown', poll: false };
    const waiting = cli ? 'confirmPlain.publishing.waitCli' : 'confirmPlain.publishing.waitChat';
    // AI の接続の「1 分たっても…チャットに戻って『押した』と送って」は最初から出す（Web の Claude は送るまで進まない = 待たせない・nextStepKey の WHY）
    // 遅いときは「ふつうは数秒で終わります」を外す（下の「いつもより時間がかかっています」と食い違うため）
    if (cli && elapsedMs >= SLOW_AFTER_MS) return { phase: 'slow', title: `confirmPlain.publishing.title${test}`, line: 'confirmPlain.publishing.waitCliSlow', next: 'confirmPlain.publishing.slowCli', poll: true };
    return { phase: 'working', title: `confirmPlain.publishing.title${test}`, line: waiting, next: cli ? 'confirmPlain.publishing.leaveOpen' : 'confirmAction.next.chat', poll: true };
  }
  if (status === 'used') {
    return {
      phase: 'done',
      title: `confirmPlain.published.title${test}`,
      line: hasUrl ? `confirmPlain.published.at${test}` : `confirmPlain.published.noUrl${test}`,
      ...(hasUrl ? { open: `confirmPlain.published.open${test}` } : {}),
      poll: false
    };
  }
  // 公開されないまま終わった。何が起きたか = 今までの confirmAction.status.* の文（AI の接続向け）。CLI は「同じコマンドをもう一度」が次の手
  // （confirmAction.status.expired は「AI とのチャットに戻って」と言う = CLI の人には合わない）
  if (status === 'denied') return { phase: 'stopped', title: 'confirmPlain.stopped.cancelledTitle', line: 'confirmAction.status.denied', poll: false };
  const line = cli && (status === 'expired' || status === 'stale') ? `confirmPlain.stopped.${status}Cli` : `confirmAction.status.${status}`;
  return { phase: 'stopped', title: 'confirmPlain.stopped.title', line, poll: false };
}
